import * as React from "react";
import { Alert, Input, Modal, notification, Select } from "antd";
import { linksOf, request } from "../../../services/cpln";
import {
  ShrinkVolumeReducerState,
  RunShrinkVolumeReducerActionType,
  useRunShrinkVolumeReducer,
} from "./reducer_shrinkVolume";
import { VolumeSet } from "../../../schema/types/volumeSet/entity";

interface Props {
  volumeSet: VolumeSet;
  initialLocation?: string;
  initialVolumeIndex?: string;
  initialStorageCapacity?: number;
  onClose: any;
  onDone: any;
  visible: boolean;
}

export const CommandModal_ShrinkVolume: React.FC<Props> = ({
  volumeSet,
  initialLocation,
  initialVolumeIndex,
  initialStorageCapacity,
  onClose,
  onDone,
  visible,
}) => {
  const selfLink = linksOf(volumeSet).self!;
  const [isLoading, setIsLoading] = React.useState(false);
  const [confirmationText, setConfirmationText] = React.useState("");
  const [state, dispatch] = useRunShrinkVolumeReducer(
    initialLocation,
    initialVolumeIndex,
    initialStorageCapacity !== undefined ? String(initialStorageCapacity) : undefined,
    undefined,
  );

  async function handleConfirm() {
    try {
      setIsLoading(true);
      const spec: any = {
        location: state.location,
        volumeIndex: Number(state.volumeIndex),
        newStorageCapacity: Number(state.newStorageCapacity),
        timeoutSeconds: Number(state.timeoutSeconds),
      };
      await request({
        method: "post",
        url: `${selfLink}/-command`,
        body: {
          type: "shrinkVolume",
          spec: spec,
        },
      });
      onDone();
      notification.success({
        message: "Success",
        description: "Created Command.",
      });
    } catch (e) {
      let errorMessage = e?.response?.data?.message;
      if (!errorMessage) errorMessage = e.message;
      notification.warning({
        message: "Failed",
        description: errorMessage,
      });
      setIsLoading(false);
    }
  }

  const CONFIRMATION_TOKEN = React.useMemo(() => {
    return `${volumeSet.name}/${state.location}/${state.volumeIndex}`;
  }, [volumeSet.name, state.location, state.volumeIndex]);

  let isValid = true;
  if (!state.location) isValid = false;
  if (!state.volumeIndex) isValid = false;
  if (!state.newStorageCapacity) isValid = false;
  if (confirmationText !== CONFIRMATION_TOKEN) isValid = false;

  return (
    <Modal
      width={450}
      open={visible}
      title="Shrink Volume"
      okText={`Shrink`}
      cancelButtonProps={{ type: "default", danger: false, disabled: isLoading }}
      okButtonProps={{ type: "primary", danger: true, disabled: isLoading || !isValid, loading: isLoading }}
      onOk={handleConfirm}
      onCancel={onClose}
      maskClosable={false}
      destroyOnHidden
    >
      <Alert
        type="warning"
        className="mb-4"
        message="This operation will replace the volume, which will cause data loss. The volume will be recreated with a smaller capacity."
      />
      <div>Location</div>
      <Select
        className="mb-4 w-full"
        value={state.location}
        onChange={(value) => dispatch({ type: RunShrinkVolumeReducerActionType.LOCATION, payload: value })}
        options={(volumeSet.status?.locations || []).map(({ name: l }) => ({
          label: l,
          value: l,
          disabled: false,
          isLabel: false,
        }))}
      />
      <div>Volume Index</div>
      <Input
        className="mb-4"
        name={"volumeIndex"}
        value={state.volumeIndex}
        onChange={(e) => dispatch({ type: RunShrinkVolumeReducerActionType.INDEX, payload: e.target.value })}
      />
      <div>New Storage Capacity (GiB)</div>
      <Input
        className="mb-4"
        name={"newStorageCapacity"}
        value={state.newStorageCapacity}
        onChange={(e) =>
          dispatch({ type: RunShrinkVolumeReducerActionType.NEW_STORAGE_CAPACITY, payload: e.target.value })
        }
      />
      <div>Timeout Seconds</div>
      <Input
        className="mb-4"
        name={"timeoutSeconds"}
        value={state.timeoutSeconds}
        onChange={(e) =>
          dispatch({ type: RunShrinkVolumeReducerActionType.TIMEOUT_SECONDS, payload: e.target.value })
        }
      />
      <div className="delete-modal-confirmation-warning mt-4">{`Type "${CONFIRMATION_TOKEN}" to confirm`}</div>
      <Input
        value={confirmationText}
        onChange={(e) => setConfirmationText(e.target.value)}
        placeholder={`confirmation`}
      />
    </Modal>
  );
};
